import { NsContent } from '@britishgasenergy/react-nucleus/dist';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import NsColumn from '@britishgasenergy/react-nucleus/dist/nuc-components/NsColumn';
import { NsCard } from 'components/common/ns-card/NsCard';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { NsCaveat } from 'components/common/ns-caveat/NsCaveat';
import { AboutHeatPump } from 'components/direct-sales/heat-pump-registration/components/AboutHeatPump';
import { HeatPumpFAQ } from 'components/direct-sales/heat-pump-registration/components/HeatPumpFAQ';
import { RegistrationForm } from 'components/common/registration-form/RegistrationForm';
import {
  setFormField,
  selectDetails,
  selectErrors,
  selectIsLoading
} from 'state/heat-pump-registration/heat-pump-registration-slice';
import { sendHeatPumpSignupData } from 'state/heat-pump-registration/heat-pump-registration-thunks';
import { useNavigation } from 'hooks/useNavigation';
import { NsCaveatLink } from 'components/common/ns-caveat-link/NsCaveatLink';
import { useDDSavingsAmount } from 'hooks/useDDSavingsAmount';

export const testId = 'heatPumpRegistrationTestId';

export const HeatPumpRegistration = () => {
  const dispatch = useDispatch();
  const { heatPumpAmount, priceCapDate } = useDDSavingsAmount();
  const apiErrors = useSelector(selectErrors, shallowEqual);
  const details = useSelector(selectDetails, shallowEqual);
  const isLoading = useSelector(selectIsLoading, shallowEqual);
  const { next, eventTypes } = useNavigation();

  const handleGoNext = async (e) => {
    e.preventDefault();

    try {
      await dispatch(sendHeatPumpSignupData(details));
      next({ eventType: eventTypes.NEXT });
    } catch (e) {
      e && console.error(e);
    }
  };

  const handleChange = (event) => {
    const {
      target: { name, processedValue: value }
    } = event;

    dispatch(setFormField({ value, name }));
  };

  return (
    <>
      <NsPanel decoration="grey-light" testId={testId}>
        <NsContent>
          <NsTitle title="Sign up to our free heat pump tariff add-on" />
          <p className="p-normal">
            Exclusively for homes with a British gas-installed heat pump. You
            could save money on the electricity your heat pump uses with our{' '}
            <b>free heat pump tariff add-on.</b> Simply sign up with your
            details and we&apos;ll take care of the rest.
          </p>
        </NsContent>
        <NsColumn hockeypitch="2">
          <NsCard
            type="section"
            decoration="savings"
            heading="Get a discount on your electricity"
          >
            <p>
              Get a discounted unit rate of 14p/kWh for the electricity your
              heat pump uses and you could save up to {heatPumpAmount}
              <NsCaveatLink>
                Up to {heatPumpAmount} energy saving: Compares customer with D
                rated boiler (yearly energy usage: gas 11,500kWh, electricity
                2,700 kWh; no gas used for cooking) to customer with air source
                heat pump (SCOP 350%; yearly electricity usage of 5,411 kWh; no
                longer uses gas). Both customers paying UK average{' '}
                {priceCapDate} Direct Debit price cap rates.
              </NsCaveatLink>{' '}
              over 12 months.
            </p>
          </NsCard>
          <NsCard
            type="section"
            decoration="payments"
            heading="Save every month"
          >
            <p>
              The discount will be added as a credit to your statement each
              month for 12 months.
              <NsCaveatLink>
                If you’re on a time-of-use tariff (e.g. Electric Driver or an
                Economy 7 tariff) we’ll assume that all electricity used by your
                heat pump was used at peak times.
              </NsCaveatLink>
            </p>
          </NsCard>
          <NsCard
            type="section"
            decoration="rosette-tick-green"
            heading="Free and flexible"
          >
            <p>
              There&apos;s no extra cost for the energy offer and you can still
              switch your tariff to any of our fixed tariffs for free at any
              time.
            </p>
          </NsCard>
          <NsCard
            type="section"
            decoration="gas"
            heading="Want to save even more?"
          >
            <p>
              We can cap your gas supply for free if you no longer use gas at
              your property.
            </p>
          </NsCard>
        </NsColumn>
      </NsPanel>
      <AboutHeatPump />

      <RegistrationForm
        details={details}
        handleChange={handleChange}
        handleGoNext={handleGoNext}
        errors={apiErrors}
        isLoading={isLoading}
        termsPdf="https://www.britishgas.co.uk/aem6/content/dam/britishgas/documents/Heat-Pump-Add-On-Terms-and-Conditions.pdf"
        termsPdfText="Heat pump tariff add-on Terms"
        termsCheckboxLabel={`
          I have read and understood the Terms and Conditions for the 
          heat pump add-on.`}
        formOfferName="heat pump add-on"
      />
      <HeatPumpFAQ />
      <NsCaveat heading="Legal information">
        <p>
          Here&apos;s full{' '}
          <NsAnchor href="https://www.britishgas.co.uk/aem6/content/dam/britishgas/documents/Heat-Pump-Add-On-Terms-and-Conditions.pdf">
            terms and conditions
          </NsAnchor>{' '}
          of the heat pump add-on.
        </p>
      </NsCaveat>
    </>
  );
};

export default HeatPumpRegistration;
